import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, FileText, Download, Search as SearchIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import Header from '@/components/Header';
import * as XLSX from 'xlsx';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MemberReports = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [members, setMembers] = useState([]);
  const [sortColumn, setSortColumn] = useState('member_id');
  const [sortDirection, setSortDirection] = useState('asc');
  const [availableStates, setAvailableStates] = useState([]);
  const [availableDistricts, setAvailableDistricts] = useState([]);
  const [searchCriteria, setSearchCriteria] = useState({
    database_type: '',
    member_type: '',
    customer_type: '',
    member_id: '',
    name: '',
    company_name: '',
    email: '',
    designation: '',
    status: '',
    expiry_filter_type: ''
  });

  const handleSearch = async (e) => {
    if (e) e.preventDefault();
    setLoading(true);
    
    try {
      // Prepare search data, converting "all-states" and "all-districts" to empty strings
      const searchData = {
        ...searchCriteria,
        state: searchCriteria.state === 'all-states' ? '' : searchCriteria.state,
        district: searchCriteria.district === 'all-districts' ? '' : searchCriteria.district
      };
      
      const response = await axios.post(`${API}/members/search`, searchData);
      setMembers(response.data);
      
      if (response.data.length === 0) {
        toast.info('No members found matching the criteria');
      } else {
        toast.success(`Found ${response.data.length} member(s)`);
      }
    } catch (error) {
      console.error('Error searching members:', error);
      toast.error('Failed to search members');
    } finally {
      setLoading(false);
    }
  };

  // Fetch available states based on current filters
  const fetchAvailableStates = async () => {
    try {
      const filterData = {
        database_type: searchCriteria.database_type,
        customer_type: searchCriteria.customer_type,
        status: searchCriteria.status
      };
      const response = await axios.post(`${API}/members/unique-states`, filterData);
      setAvailableStates(response.data.states || []);
    } catch (error) {
      console.error('Error fetching states:', error);
      setAvailableStates([]);
    }
  };

  // Fetch available districts based on current filters and selected state
  const fetchAvailableDistricts = async () => {
    if (!searchCriteria.state) {
      setAvailableDistricts([]);
      return;
    }
    
    try {
      const filterData = {
        database_type: searchCriteria.database_type,
        customer_type: searchCriteria.customer_type,
        status: searchCriteria.status,
        state: searchCriteria.state
      };
      const response = await axios.post(`${API}/members/unique-districts`, filterData);
      setAvailableDistricts(response.data.districts || []);
    } catch (error) {
      console.error('Error fetching districts:', error);
      setAvailableDistricts([]);
    }
  };

  // Fetch states when relevant filters change
  useEffect(() => {
    if (searchCriteria.database_type || searchCriteria.customer_type || searchCriteria.status) {
      fetchAvailableStates();
    } else {
      setAvailableStates([]);
      setAvailableDistricts([]);
      setSearchCriteria(prev => ({ ...prev, state: '', district: '' }));
    }
  }, [searchCriteria.database_type, searchCriteria.customer_type, searchCriteria.status]);

  // Fetch districts when state changes
  useEffect(() => {
    if (searchCriteria.state) {
      fetchAvailableDistricts();
    } else {
      setAvailableDistricts([]);
      setSearchCriteria(prev => ({ ...prev, district: '' }));
    }
  }, [searchCriteria.state]);

  const handleSort = (column) => {
    if (sortColumn === column) {
      // Toggle sort direction or reset
      if (sortDirection === 'asc') {
        setSortDirection('desc');
      } else if (sortDirection === 'desc') {
        setSortColumn('');
        setSortDirection('asc');
      }
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const getSortedMembers = () => {
    if (!sortColumn) return members;

    return [...members].sort((a, b) => {
      let aVal = a[sortColumn] || '';
      let bVal = b[sortColumn] || '';

      // Special handling for member_id - extract numeric part
      if (sortColumn === 'member_id') {
        const extractNumber = (id) => {
          const match = id.match(/\d+/);
          return match ? parseInt(match[0]) : 0;
        };
        aVal = extractNumber(aVal);
        bVal = extractNumber(bVal);
      }
      // Handle date columns
      else if (sortColumn === 'created_at' || sortColumn === 'updated_at' || sortColumn === 'expiry_date') {
        aVal = aVal ? new Date(aVal).getTime() : 0;
        bVal = bVal ? new Date(bVal).getTime() : 0;
      }
      // Handle string columns
      else if (typeof aVal === 'string') {
        aVal = aVal.toLowerCase();
        bVal = bVal.toLowerCase();
      }

      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const handleReset = () => {
    setSearchCriteria({
      database_type: '',
      member_type: '',
      customer_type: '',
      member_id: '',
      name: '',
      company_name: '',
      email: '',
      designation: '',
      status: ''
    });
    setMembers([]);
    setSortColumn('');
    setSortDirection('asc');
  };

  const handleResetOld = () => {
    setSearchCriteria({
      database_type: '',
      member_type: '',
      member_id: '',
      name: '',
      company_name: '',
      email: '',
      designation: ''
    });
    setMembers([]);
  };

  const handlePrint = () => {
    // Set document title for PDF filename
    document.title = 'Member Report';
    window.print();
    // Reset title after print
    setTimeout(() => { document.title = 'Magazine CRM'; }, 1000);
  };

  const exportToCSV = () => {
    if (members.length === 0) {
      toast.error('No data to export');
      return;
    }

    try {
      // Get sorted members to export in the same order as displayed
      const sortedMembers = getSortedMembers();
      
      // Prepare data for Excel
      const headers = ['ID', 'Name', 'Initials', 'Designation', 'Company', 'Contact', 'WhatsApp No', 'Email', 'Address', 'Pincode', 'State', 'District', 'Expiry Date', 'Member Type', 'Customer Type', 'Plan Type', 'Magazine', 'Subscription Duration', 'Amount Paid', 'Status', 'Notes', 'Category', 'Delivery', 'Created Date', 'Last Modified'];
      
      const data = sortedMembers.map(member => [
        member.member_id || '',
        member.name || '',
        member.initials || '',
        member.designation || '',
        member.company_name || '',
        member.contact_no || '',
        member.whatsapp_no || '',
        member.email || '',
        member.address || '',
        member.pincode || '',
        member.state || '',
        member.district || '',
        member.expiry_date || '',
        member.member_type || '',
        member.customer_type || '',
        member.plan_type || '',
        member.database_type || '',
        member.subscription_duration || '',
        member.amount_paid || '',
        member.status || '',
        member.notes || '',
        member.category || '',
        member.delivery || '',
        member.created_at ? new Date(member.created_at).toLocaleString('en-IN') : '',
        member.updated_at ? new Date(member.updated_at).toLocaleString('en-IN') : ''
      ]);

      // Create Excel workbook
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.aoa_to_sheet([headers, ...data]);
      XLSX.utils.book_append_sheet(wb, ws, 'Member Reports');
      
      // Download Excel file with descriptive name
      XLSX.writeFile(wb, `Member_Report_${new Date().toISOString().split('T')[0]}.xlsx`);
      
      toast.success('Excel report downloaded!');
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export report');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50 py-8">
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex items-center justify-between mb-8 no-print">
          <div className="flex items-center space-x-4">
            <Button variant="outline" onClick={() => navigate('/')} data-testid="back-button">
              <ArrowLeft className="mr-2" size={18} />
              Back
            </Button>
            <h1 className="text-4xl font-bold" style={{ color: 'var(--primary-blue)' }} data-testid="reports-title">
              Member Reports
            </h1>
          </div>
          {members.length > 0 && (
            <div className="flex space-x-2">
              <Button onClick={exportToCSV} className="btn-secondary" data-testid="export-button">
                <Download className="mr-2" size={18} />
                Export to Excel
              </Button>
              <Button onClick={handlePrint} className="btn-primary" data-testid="print-button">
                <FileText className="mr-2" size={18} />
                Print Report
              </Button>
            </div>
          )}
        </div>

        {/* Search Form */}
        <form onSubmit={handleSearch} className="form-section mb-8 no-print">
          <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--primary-blue)' }}>Search Filters</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="space-y-2">
              <Label>Magazine</Label>
              <Select value={searchCriteria.database_type} onValueChange={(value) => setSearchCriteria({ ...searchCriteria, database_type: value })}>
                <SelectTrigger data-testid="database-type-filter">
                  <SelectValue placeholder="Select magazine" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Aabkari Times">Aabkari Times</SelectItem>
                  <SelectItem value="Aabkari Times ENG">Aabkari Times ENG</SelectItem>
                  <SelectItem value="Sugar Times">Sugar Times</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Customer Type</Label>
              <Select value={searchCriteria.customer_type} onValueChange={(value) => setSearchCriteria({ ...searchCriteria, customer_type: value })}>
                <SelectTrigger data-testid="customer-type-filter">
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Member">Member</SelectItem>
                  <SelectItem value="Complimentary">Complimentary</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Status</Label>
              <Select value={searchCriteria.status} onValueChange={(value) => setSearchCriteria({ ...searchCriteria, status: value })}>
                <SelectTrigger data-testid="status-filter">
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ACTIVE">ACTIVE</SelectItem>
                  <SelectItem value="INACTIVE">INACTIVE</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Expiry Filter</Label>
              <Select value={searchCriteria.expiry_filter_type || 'all'} onValueChange={(value) => setSearchCriteria({ ...searchCriteria, expiry_filter_type: value === 'all' ? '' : value })}>
                <SelectTrigger data-testid="expiry-filter">
                  <SelectValue placeholder="All Members" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Members</SelectItem>
                  <SelectItem value="expired">Expired Members</SelectItem>
                  <SelectItem value="expiring_3months">Expiring Within 3 Months</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>State</Label>
              <Select 
                value={searchCriteria.state} 
                onValueChange={(value) => setSearchCriteria({ ...searchCriteria, state: value, district: '' })}
                disabled={availableStates.length === 0}
              >
                <SelectTrigger data-testid="state-filter">
                  <SelectValue placeholder={availableStates.length > 0 ? "Select state" : "Select filters first"} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all-states">All States</SelectItem>
                  {availableStates.map(state => (
                    <SelectItem key={state} value={state}>{state}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>District</Label>
              <Select 
                value={searchCriteria.district} 
                onValueChange={(value) => setSearchCriteria({ ...searchCriteria, district: value })}
                disabled={!searchCriteria.state || availableDistricts.length === 0}
              >
                <SelectTrigger data-testid="district-filter">
                  <SelectValue placeholder={searchCriteria.state ? (availableDistricts.length > 0 ? "Select district" : "No districts") : "Select state first"} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all-districts">All Districts</SelectItem>
                  {availableDistricts.map(district => (
                    <SelectItem key={district} value={district}>{district}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Member ID</Label>
              <Input data-testid="member-id-input" value={searchCriteria.member_id} onChange={(e) => setSearchCriteria({ ...searchCriteria, member_id: e.target.value })} placeholder="Enter member ID" />
            </div>

            <div className="space-y-2">
              <Label>Name</Label>
              <Input data-testid="name-input" value={searchCriteria.name} onChange={(e) => setSearchCriteria({ ...searchCriteria, name: e.target.value })} placeholder="Enter name" />
            </div>

            <div className="space-y-2">
              <Label>Company Name</Label>
              <Input data-testid="company-name-input" value={searchCriteria.company_name} onChange={(e) => setSearchCriteria({ ...searchCriteria, company_name: e.target.value })} placeholder="Enter company name" />
            </div>

            <div className="space-y-2">
              <Label>Email</Label>
              <Input data-testid="email-input" value={searchCriteria.email} onChange={(e) => setSearchCriteria({ ...searchCriteria, email: e.target.value })} placeholder="Enter email" />
            </div>

            <div className="space-y-2">
              <Label>Designation</Label>
              <Input data-testid="designation-input" value={searchCriteria.designation} onChange={(e) => setSearchCriteria({ ...searchCriteria, designation: e.target.value })} placeholder="Enter designation" />
            </div>
          </div>

          <div className="mt-8 flex space-x-4">
            <Button type="submit" disabled={loading} className="btn-primary" data-testid="search-button">
              <SearchIcon className="mr-2" size={18} />
              {loading ? 'Searching...' : 'Search'}
            </Button>
            <Button type="button" onClick={handleReset} variant="outline" data-testid="reset-button">
              Reset
            </Button>
          </div>
        </form>

        {/* Report Table */}
        {loading ? (
          <div className="loader" />
        ) : members.length > 0 ? (
          <div className="form-section">
            <h2 className="text-2xl font-bold mb-4" style={{ color: 'var(--primary-blue)' }}>
              Total Members: {members.length}
            </h2>
            <div style={{ overflowX: 'auto', width: '100%' }}>
              <table className="data-table" data-testid="reports-table" style={{ minWidth: '800px' }}>
                <thead>
                  <tr>
                    <th onClick={() => handleSort('member_id')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                      ID {sortColumn === 'member_id' && <span style={{ marginLeft: '4px' }}>{sortDirection === 'asc' ? '▲' : '▼'}</span>}
                    </th>
                    <th onClick={() => handleSort('name')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                      Name {sortColumn === 'name' && <span style={{ marginLeft: '4px' }}>{sortDirection === 'asc' ? '▲' : '▼'}</span>}
                    </th>
                    <th onClick={() => handleSort('designation')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                      Designation {sortColumn === 'designation' && <span style={{ marginLeft: '4px' }}>{sortDirection === 'asc' ? '▲' : '▼'}</span>}
                    </th>
                    <th onClick={() => handleSort('company_name')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                      Company {sortColumn === 'company_name' && <span style={{ marginLeft: '4px' }}>{sortDirection === 'asc' ? '▲' : '▼'}</span>}
                    </th>
                    <th onClick={() => handleSort('district')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                      District {sortColumn === 'district' && <span style={{ marginLeft: '4px' }}>{sortDirection === 'asc' ? '▲' : '▼'}</span>}
                    </th>
                    <th onClick={() => handleSort('contact_no')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                      Contact No. {sortColumn === 'contact_no' && <span style={{ marginLeft: '4px' }}>{sortDirection === 'asc' ? '▲' : '▼'}</span>}
                    </th>
                    <th onClick={() => handleSort('whatsapp_no')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                      WhatsApp No. {sortColumn === 'whatsapp_no' && <span style={{ marginLeft: '4px' }}>{sortDirection === 'asc' ? '▲' : '▼'}</span>}
                    </th>
                    <th onClick={() => handleSort('expiry_date')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                      Expiry Date {sortColumn === 'expiry_date' && <span style={{ marginLeft: '4px' }}>{sortDirection === 'asc' ? '▲' : '▼'}</span>}
                    </th>
                    <th onClick={() => handleSort('email')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                      Email {sortColumn === 'email' && <span style={{ marginLeft: '4px' }}>{sortDirection === 'asc' ? '▲' : '▼'}</span>}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {getSortedMembers().map((member) => (
                    <tr key={member.id} data-testid={`report-row-${member.member_id}`}>
                      <td>{member.member_id}</td>
                      <td style={{ wordWrap: 'break-word', whiteSpace: 'normal', maxWidth: '150px' }}>{member.name}</td>
                      <td>{member.designation}</td>
                      <td style={{ wordWrap: 'break-word', whiteSpace: 'normal', maxWidth: '120px' }}>{member.company_name}</td>
                      <td>{member.district}</td>
                      <td style={{ wordWrap: 'break-word', whiteSpace: 'normal', minWidth: '100px' }}>{member.contact_no || '-'}</td>
                      <td style={{ wordWrap: 'break-word', whiteSpace: 'normal', minWidth: '100px' }}>{member.whatsapp_no || '-'}</td>
                      <td>{member.expiry_date}</td>
                      <td style={{ wordWrap: 'break-word', whiteSpace: 'normal', maxWidth: '180px' }}>{member.email}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <div className="form-section">
            <p className="text-gray-600 text-center">Use search filters above to find members and generate reports.</p>
          </div>
        )}
      </div>
      
      <style>{`
        @media print {
          @page {
            size: A4 landscape;
            margin: 10mm;
          }
          
          .no-print {
            display: none !important;
          }
          
          body {
            print-color-adjust: exact;
            -webkit-print-color-adjust: exact;
          }
          
          table {
            font-size: 9px !important;
            width: 100% !important;
          }
          
          th, td {
            padding: 3px !important;
            font-size: 9px !important;
          }
          
          * {
            color: black !important;
          }
          
          h1, h2 {
            color: black !important;
          }
          
          th {
            background-color: white !important;
            color: black !important;
            border: 1px solid black !important;
          }
          
          td {
            border: 1px solid #ccc !important;
          }
        }
      `}</style>
    </div>
  );
};

export default MemberReports;